import React, { lazy } from 'react';

import './App.css';
import { Route, Routes, Outlet } from 'react-router-dom';
import useAuthErrorHandler from './components/Auth/useAuthErrorHandler';
import useUserTeamWebSocket from './hooks/useUserTeamWebSocket';
import useUserPreferences from './hooks/useUserPreferences';
import { useSelector } from 'react-redux';
import ROUTES from './constants/ROUTES';

// Components
import Auth from './components/Auth';
import Wrapper from './components/Wrapper';
import SetPassword from './pages/SetPassword';
import { GROUPS, USERSTYPES } from './constants/ROLES';

// Lazy Loading Components
const Login = lazy(() => import('./pages/Login'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SessionExpired = lazy(() => import('./pages/SessionExpired'));
const Onboarding = lazy(() => import('./pages/Onboarding'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const CreateOrder = lazy(() => import('./pages/CreateOrder'));
const OrderDetails = lazy(() => import('./pages/OrderDetails'));
const NewProfile = lazy(() => import('./pages/Profile/NewProfile'));
const Profile = lazy(() => import('./pages/Profile'));
const AllProfiles = lazy(() => import('./pages/AllProfiles'));
const AllOrders = lazy(() => import('./pages/AllOrders'));
const JurisdictionCredentials = lazy(
  () => import('./pages/JurisdictionCredentials')
);
const DocumentTypeManagement = lazy(
  () => import('./pages/DocumentTypeManagement')
);
const DocumentTypeVisibility = lazy(
  () => import('./pages/DocumentTypeVisibility')
);
const DocumentCategoryMapping = lazy(
  () => import('./pages/DocumentCategoryMapping')
);
const ProductNameManagement = lazy(
  () => import('./pages/ProductNameManagement')
);
const SiteMap = lazy(() => import('./pages/SiteMap'));
const BulkOrderCheck = lazy(() => import('./pages/BulkOrderCheck'));
const AllProject = lazy(() => import('./pages/AllProjects'));
const NewProject = lazy(() => import('./pages/NewProject'));
const ProjectDetails = lazy(() => import('./pages/ProjectDetails'));
const Mailer = lazy(() => import('./pages/Mailer'));
const ComposeEmail = lazy(() => import('./pages/ComposeEmail'));
const EmailSignature = lazy(() => import('./pages/EmailSignature'));
const RateCalculator = lazy(() => import('./pages/RateCalculator'));
const Teams = lazy(() => import('./pages/Teams'));
const People = lazy(() => import('./pages/People'));
const NotFound = lazy(() => import('./pages/NotFound'));

const WrapperLayout = () => (
  <Wrapper>
    <Outlet />
  </Wrapper>
);

const App = () => {
  const { userDetails } = useSelector((state) => state?.userDetails);

  useAuthErrorHandler();
  useUserTeamWebSocket();
  const isAuthenticated = Object.keys(userDetails)?.length > 0;
  useUserPreferences(userDetails?.user?.id);
  const userRole = userDetails?.user?.role?.label;
  const isAdmin = GROUPS.ADMINACCESSGROUP.includes(userRole);
  const isClientAdmin = USERSTYPES?.Client_Admin === userRole;
  const isInternalAdmin = USERSTYPES?.ADMIN === userRole;

  return (
    <Auth>
      {/* Keyed by user so an impersonation switch remounts every page */}
      <Routes key={userDetails?.user?.id}>
        {isAuthenticated ? (
          <Route element={<WrapperLayout />}>
            {isAdmin && (
              <>
                <Route path='/' element={<Dashboard />} />
                <Route path={ROUTES.newProfile} element={<NewProfile />} />
                <Route
                  path={`${ROUTES.mailer}/:box/:teamID`}
                  element={<Mailer />}
                />
                <Route path={ROUTES.composeEmail} element={<ComposeEmail />} />
                <Route path={ROUTES.teams} element={<Teams />} />
                <Route path={ROUTES.people} element={<People />} />
                <Route path={ROUTES.allProfiles} element={<AllProfiles />} />
                <Route
                  path={`${ROUTES.allProfiles}/:goToSearch`}
                  element={<AllProfiles />}
                />

                {isInternalAdmin && (
                  <>
                    <Route
                      path={ROUTES.jurisdictionCredentials}
                      element={<JurisdictionCredentials />}
                    />
                    <Route
                      path={ROUTES.emailSignature}
                      element={<EmailSignature />}
                    />
                    <Route
                      path={ROUTES.documentTypeManagement}
                      element={<DocumentTypeManagement />}
                    />
                    <Route
                      path={ROUTES.documentTypeVisibility}
                      element={<DocumentTypeVisibility />}
                    />
                    <Route
                      path={ROUTES.documentTypeMapping}
                      element={<DocumentCategoryMapping />}
                    />
                    <Route
                      path={ROUTES.productNameManagement}
                      element={<ProductNameManagement />}
                    />
                  </>
                )}
              </>
            )}
            {(isAdmin || isClientAdmin) && (
              <Route
                path={`${ROUTES.profile}/:profileID`}
                element={<Profile />}
              />
            )}
            <Route path={ROUTES.createOrder} element={<CreateOrder />} />
            <Route
              path={`${ROUTES.orderDetails}/:orderID`}
              element={<OrderDetails />}
            />
            <Route
              path={`${ROUTES.orderDetails}/:sectionName/:orderID`}
              element={<OrderDetails />}
            />
            <Route path='/' element={<ClientDashboard />} />
            <Route path={`${ROUTES.allOrders}`} element={<AllOrders />} />
            <Route
              path={`${ROUTES.allOrders}/:goToSearch`}
              element={<AllOrders />}
            />
            <Route path={ROUTES.allProjects} element={<AllProject />} />
            <Route
              path={`${ROUTES.allProjects}/:goToSearch`}
              element={<AllProject />}
            />
            <Route path={ROUTES.newProject} element={<NewProject />} />
            <Route
              path={`${ROUTES.projectDetails}/:projectID`}
              element={<ProjectDetails />}
            />
            <Route
              path={`${ROUTES.projectDetails}/:sectionName/:projectID`}
              element={<ProjectDetails />}
            />
            <Route path={ROUTES.siteMap} element={<SiteMap />} />
            <Route path={ROUTES.bulkOrderCheck} element={<BulkOrderCheck />} />
            <Route path={ROUTES.rateCalculator} element={<RateCalculator />} />
            <Route path='*' element={<NotFound />} />
          </Route>
        ) : (
          <>
            {/* Public Routes */}
            <Route path={ROUTES.login} element={<Login />} />
            <Route path={`${ROUTES.login}/:link`} element={<Login />} />
            <Route path={ROUTES.resetPassword} element={<ResetPassword />} />
            <Route path={ROUTES.sessionExpired} element={<SessionExpired />} />
          </>
        )}
        {/* Common Routes */}
        <Route
          path={`${ROUTES.setPassword}/:uniqueID`}
          element={<SetPassword />}
        />
        <Route
          path={`${ROUTES.onboarding}/:invitationID`}
          element={<Onboarding />}
        />
      </Routes>
    </Auth>
  );
};

export default App;
